import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { deepCompare } from "../../helper/object-helper";
import {
	UpdateSiteFloorParams,
	VelavuAPI,
	VelavuFloor,
	VelavuSite,
} from "velavu-js-api";
import {
	FloorplanImage,
	floorplanImageFromFloor,
} from "../../data/floorplan-image";
import UserImageData, {
	userImageDataFromFloor,
	userImageDataToBase64,
} from "../../data/user-image-data";
import { compareArrays } from "../../helper/array-helper";
import { projectToPlane, projectToSphere } from "../../helper/geo-helper";
import { fileToPlaceableImage } from "../../helper/image-render-helper";
import {
	floorWallsToVelavuFloorGeometry,
	velavuFloorGeometryToFloorWalls,
} from "./floor-wall-helper";
import ImagePositioning, {
	DEFAULT_IMAGE_POSITIONING,
	imagePositioningToEdgeCoords,
} from "./image-positioning";
import PlanToolType from "./plan-tool-type";
import ReferenceFloor from "./reference-floor";

export interface FloorEditState {
	site: VelavuSite | null;
	floor: VelavuFloor | null;

	open: (site: VelavuSite, floor: VelavuFloor) => void;
	close: () => void;
	submit: () => void;

	mode: FloorEditStateMode;
	switchMode: (mode: FloorEditStateMode) => void;

	isPositioning: boolean;
	addImage: (file: File) => void;
	replaceImage: (file: File) => void;
	repositionImage: () => void;
	finishPositioning: () => void;
	abandonPositioning: () => void;
	removeImage: () => void;

	dataState: FloorEditDataState;

	info: FloorEditInfoState;
	setInfo: Dispatch<SetStateAction<FloorEditInfoState>>;

	opacity: number;
	setOpacity: Dispatch<SetStateAction<number>>;

	positioning: ImagePositioning;
	setPositioning: Dispatch<SetStateAction<ImagePositioning>>;

	image: UserImageData | null;

	// The currently selected drawing tool
	drawToolType: PlanToolType;
	setDrawToolType: Dispatch<SetStateAction<PlanToolType>>;

	// The walls being drawn, represented as an array of arrays of points
	drawWalls: [number, number][][];
	setDrawWalls: Dispatch<SetStateAction<[number, number][][]>>;

	//The ID of the floor to use as a reference while drawing walls
	referenceFloorID: string | null;
	setReferenceFloorID: Dispatch<SetStateAction<string | null>>;

	// The floorplan image to display on the map
	referenceImage: FloorplanImage | null;

	// The floors selectable as reference floors
	referenceFloors: ReferenceFloor[];
}

export enum FloorEditStateMode {
	Info,
	Image,
	Draw,
}

export enum FloorEditDataState {
	Idle,
	Dirty,
	Saving,
	Saved,
}

export interface FloorEditInfoState {
	level: number;
	name: string;
}

export function useFloorEditState(
	onUpdateFloor: (floor: VelavuFloor, site: VelavuSite) => void,
): FloorEditState {
	const [site, setSite] = useState<VelavuSite | null>(null);
	const [floor, setFloor] = useState<VelavuFloor | null>(null);
	const [mode, setMode] = useState<FloorEditStateMode>(
		FloorEditStateMode.Info,
	);
	const [isPositioning, setIsPositioning] = useState(false);
	const [info, setInfo] = useState<FloorEditInfoState>({
		level: 0,
		name: "",
	});
	const [opacity, setOpacity] = useState<number>(1);
	const [positioning, setPositioning] = useState<ImagePositioning>(
		DEFAULT_IMAGE_POSITIONING,
	);
	const [image, setImage] = useState<UserImageData | null>(null);
	const [drawToolType, setDrawToolType] = useState<PlanToolType>("draw");
	const [drawWalls, setDrawWalls] = useState<[number, number][][]>([]);
	const [referenceFloorID, setReferenceFloorID] = useState<string | null>(
		null,
	);

	const applyFloorInfoState = useCallback((floor: VelavuFloor) => {
		setInfo({
			level: floor.level,
			name: floor.name,
		});
		if (floor.img !== undefined) {
			setReferenceFloorID(floor.id);
		} else {
			setReferenceFloorID(null);
		}
	}, []);
	const applyFloorImageState = useCallback(
		(site: VelavuSite, floor: VelavuFloor) => {
			setOpacity(floor.img?.opacity ?? 0.5);
			setPositioning({
				center: floor.img?.center ?? site.coordinates,
				rotation: floor.img?.rotation ?? 0,
				scale: floor.img?.scale ?? 1,
			});
			if (floor.img !== undefined) {
				setImage(userImageDataFromFloor(floor.img));
				setReferenceFloorID(floor.id);
			} else {
				setImage(null);
				setReferenceFloorID(null);
			}
		},
		[],
	);
	const applyFloorDrawState = useCallback((floor: VelavuFloor) => {
		setDrawToolType("draw");
		setDrawWalls(velavuFloorGeometryToFloorWalls(floor.geometry));
		if (floor.img !== undefined) {
			setReferenceFloorID(floor.id);
		} else {
			setReferenceFloorID(null);
		}
	}, []);

	const open = useCallback(
		(site: VelavuSite, floor: VelavuFloor) => {
			setSite(site);
			setFloor(floor);
			applyFloorInfoState(floor);
			applyFloorImageState(site, floor);
			applyFloorDrawState(floor);
			setMode(FloorEditStateMode.Info);
			setIsSaved(false);
		},
		[applyFloorInfoState, applyFloorImageState, applyFloorDrawState],
	);
	const close = useCallback(() => {
		setSite(null);
		setFloor(null);
	}, []);

	const switchMode = useCallback(
		(mode: FloorEditStateMode) => {
			if (!site || !floor) {
				return;
			}

			setMode(mode);
			setIsSaved(false);

			switch (mode) {
				case FloorEditStateMode.Info:
					applyFloorInfoState(floor);
					break;
				case FloorEditStateMode.Image:
					applyFloorImageState(site, floor);
					break;
				case FloorEditStateMode.Draw:
					applyFloorDrawState(floor);
					break;
			}
		},
		[
			applyFloorDrawState,
			applyFloorImageState,
			applyFloorInfoState,
			site,
			floor,
		],
	);

	const referenceImage = useMemo((): FloorplanImage | null => {
		if (!site || !floor) {
			return null;
		}

		if (mode === FloorEditStateMode.Draw || isPositioning) {
			//Show the selected floor's image
			const refFloor = site.floors?.find(
				(f) => f.id === referenceFloorID,
			);
			return floorplanImageFromFloor(refFloor);
		} else if (image !== null) {
			//Show the image of the current floor
			return {
				data: image,
				coords: imagePositioningToEdgeCoords(
					positioning,
					image.dimensions.width,
					image.dimensions.height,
				),
				opacity: opacity,
			};
		} else {
			return null;
		}
	}, [
		site,
		floor,
		mode,
		isPositioning,
		image,
		referenceFloorID,
		positioning,
		opacity,
	]);

	const isDirty = useMemo((): boolean => {
		//Return if we have no point of comparison
		if (!floor) {
			return false;
		}

		switch (mode) {
			case FloorEditStateMode.Info:
				return info.level !== floor.level || info.name !== floor.name;
			case FloorEditStateMode.Image:
				if (image) {
					return (
						floor.img === undefined ||
						//If the image is a Blob, it's a newly uploaded image
						image?.renderable instanceof Blob ||
						floor.img.opacity !== opacity ||
						floor.img.rotation !== positioning.rotation ||
						floor.img.scale !== positioning.scale ||
						!compareArrays(floor.img.center, positioning.center)
					);
				} else {
					return floor.img !== undefined;
				}
			case FloorEditStateMode.Draw: {
				//Compare the drawn walls with the original walls
				const originalWalls = velavuFloorGeometryToFloorWalls(
					floor.geometry,
				);
				return !deepCompare(originalWalls, drawWalls);
			}
		}
	}, [drawWalls, floor, image, info, mode, opacity, positioning]);

	const [isLoading, setIsLoading] = useState(false);
	const [isSaved, setIsSaved] = useState(false);
	const submit = useCallback(async () => {
		if (isLoading || !isDirty || !site || !floor) {
			return;
		}

		let request: Promise<VelavuFloor>;
		let overwriteImage = false;
		switch (mode) {
			case FloorEditStateMode.Info:
				request = VelavuAPI.siteFloors.updateSiteFloor(
					site.id,
					floor.id,
					{
						name: info.name,
						level: info.level,
					},
				);
				break;
			case FloorEditStateMode.Image:
				if (image) {
					const params: UpdateSiteFloorParams = {
						img: {
							filename: image.name,
							scale: positioning.scale,
							rotation: positioning.rotation,
							opacity: opacity,
							width: image.dimensions.width,
							height: image.dimensions.height,
							center: positioning.center,
							coordinates: imagePositioningToEdgeCoords(
								positioning,
								image.dimensions.width,
								image.dimensions.height,
							),
						},
					};
					if (image.renderable instanceof Blob) {
						//New image uploaded
						params.img_upload = await userImageDataToBase64(
							image.renderable,
						);
						overwriteImage = true;
					}

					request = VelavuAPI.siteFloors.updateSiteFloor(
						site.id,
						floor.id,
						params,
					);
				} else if (floor.img) {
					//If the floor already has an image, but the new state has no image, we need to remove it
					request = VelavuAPI.siteFloors
						.deleteFloorplanImage(site.id, floor.id)
						.then((): VelavuFloor => ({
							...floor,
							img: undefined,
						}));
				} else {
					//This shouldn't happen, but no-op just in case
					request = Promise.resolve(floor);
				}
				break;
			case FloorEditStateMode.Draw:
				request = VelavuAPI.siteFloors.updateSiteFloor(
					site.id,
					floor.id,
					{
						geometry: floorWallsToVelavuFloorGeometry(drawWalls),
					},
				);
				break;
		}

		setIsLoading(true);
		let updatedFloor: VelavuFloor;
		try {
			updatedFloor = await request;
		} catch (error) {
			console.error("Failed to update floor", error);
			return;
		} finally {
			setIsLoading(false);
		}

		// Update the image state. When uploading a new image, we'll receive a new URL
		// from the server, so we'll need to update our local image state to reflect that.
		if (overwriteImage) {
			if (updatedFloor.img !== undefined) {
				setImage(userImageDataFromFloor(updatedFloor.img));
			} else {
				setImage(null);
			}
		}

		//Update the floor state
		setFloor(updatedFloor);

		//Update the site state
		let updatedSite = site;
		const siteFloors = site.floors;
		if (siteFloors !== undefined) {
			const floorIndex = siteFloors.findIndex(
				(f) => f.id === updatedFloor.id,
			);
			if (floorIndex !== -1) {
				const floorArray = [...siteFloors];
				floorArray[floorIndex] = updatedFloor;
				floorArray.sort((a, b) => a.level - b.level);
				updatedSite = {
					...site,
					floors: floorArray,
				};
				setSite(updatedSite);
			}
		}

		//Notify the parent
		onUpdateFloor(floor, updatedSite);

		//Set the saved flag
		setIsSaved(true);
	}, [
		drawWalls,
		floor,
		image,
		info.level,
		info.name,
		isDirty,
		isLoading,
		mode,
		onUpdateFloor,
		opacity,
		positioning,
		site,
	]);

	//Used to schedule a submit after state updates have settled
	const [scheduledSubmit, setScheduledSubmit] = useState(false);
	useEffect(() => {
		if (scheduledSubmit) {
			submit();
			setScheduledSubmit(false);
		}
	}, [scheduledSubmit, submit, setScheduledSubmit]);

	const positionImage = useCallback(
		async (file: File, preservePositioning: boolean) => {
			let imageData: UserImageData;
			try {
				imageData = await fileToPlaceableImage(file);
			} catch (error) {
				alert(
					"Failed to load image. Please try again with a different file.",
				);
				console.warn("Failed to load image", error);
				return;
			}
			setImage(imageData);
			setIsPositioning(true);

			if (!preservePositioning) {
				setPositioning({
					center: site?.coordinates ?? [0, 0],
					rotation: 0,
					scale: 1,
				});
			}
		},
		[site],
	);
	const addImage = useCallback(
		(file: File) => {
			positionImage(file, false);
			if (floor?.img !== undefined) {
				setReferenceFloorID(floor.id);
			} else {
				setReferenceFloorID(null);
			}
		},
		[positionImage, floor],
	);
	const replaceImage = useCallback(
		(file: File) => {
			positionImage(file, true);
			if (floor?.img !== undefined) {
				setReferenceFloorID(floor.id);
			} else {
				setReferenceFloorID(null);
			}
		},
		[positionImage, floor],
	);
	const repositionImage = useCallback(() => {
		setIsPositioning(true);

		//If repositioning, default to using no floor as reference
		setReferenceFloorID(null);
	}, []);
	const finishPositioning = useCallback(() => {
		setIsPositioning(false);
		//Trigger a save immediately
		submit();
	}, [submit]);
	const abandonPositioning = useCallback(() => {
		setIsPositioning(false);

		//Reset all image-related state
		if (site && floor) {
			applyFloorImageState(site, floor);
		}
	}, [applyFloorImageState, site, floor]);
	const removeImage = useCallback(() => {
		setImage(null);
		setOpacity(0.5);
		setPositioning({
			center: site?.coordinates ?? [0, 0],
			rotation: 0,
			scale: 1,
		});

		//Trigger a save immediately
		setScheduledSubmit(true);
	}, [site, setScheduledSubmit]);

	const dataState = useMemo((): FloorEditDataState => {
		if (isLoading) {
			return FloorEditDataState.Saving;
		} else if (isDirty) {
			return FloorEditDataState.Dirty;
		} else if (isSaved) {
			return FloorEditDataState.Saved;
		} else {
			return FloorEditDataState.Idle;
		}
	}, [isDirty, isLoading, isSaved]);

	const referenceFloors = useMemo((): ReferenceFloor[] => {
		if (!site || !site.floors || !floor) {
			return [];
		}

		return site.floors
			.filter((floor) => floor.img !== undefined)
			.map((siteFloor): ReferenceFloor => ({
				id: siteFloor.id,
				level: siteFloor.level,
				name: siteFloor.name,
				current: siteFloor.id === floor.id,
			}));
	}, [site, floor]);

	return {
		site,
		floor,

		open,
		close,
		submit,

		mode,
		switchMode,

		isPositioning,
		addImage,
		replaceImage,
		repositionImage,
		finishPositioning,
		abandonPositioning,
		removeImage,

		dataState,

		info,
		setInfo,

		opacity,
		setOpacity,

		positioning,
		setPositioning,

		image,

		drawToolType,
		setDrawToolType,

		drawWalls,
		setDrawWalls,

		referenceFloorID,
		setReferenceFloorID,

		referenceImage,
		referenceFloors,
	};
}

function resolveCoordinateArrayCenter(
	coordinates: [number, number][] | undefined,
) {
	if (coordinates === undefined) {
		return undefined;
	}

	const sumPlane = coordinates.reduce(
		([accX, accY], [x, y]) => {
			const [planeX, planeY] = projectToPlane(x, y);
			return [accX + planeX, accY + planeY];
		},
		[0, 0],
	);
	const centerPlane: [number, number] = [
		sumPlane[0] / coordinates.length,
		sumPlane[1] / coordinates.length,
	];
	return projectToSphere(centerPlane[0], centerPlane[1]);
}
